import { type ComponentType, ReactNode, useContext } from 'react';

import {
  IconChartLine,
  IconFlag,
  IconGridApps,
  IconGridView,
  IconLab,
  IconMessages,
  type IconProps,
  IconUserCircle,
  IconVideo,
} from '@spotify-internal/encore-web';

import {
  type ResourceLinkAttributes,
  type ResourceLinkIcon,
  useResourceLinkExtensions,
} from '../../extensions';
import { ResourceNameLinkDefaultsContext } from './ResourceNameLinkDefaults';

import styles from './ResourceNameLink.module.css';

const RESOURCE_LINK_ICONS: Record<
  ResourceLinkIcon,
  ComponentType<IconProps>
> = {
  chat: IconMessages,
  entity: IconUserCircle,
  factTable: IconChartLine,
  flag: IconFlag,
  identity: IconUserCircle,
  measurement: IconChartLine,
  metric: IconChartLine,
  project: IconGridApps,
  recording: IconVideo,
  surface: IconGridView,
  workflow: IconLab,
};

type ResourceNameLinkBaseProps = ResourceLinkAttributes & {
  name: string;
  className?: string;
};

export type ResourceNameLinkProps = ResourceNameLinkBaseProps &
  (
    | {
        iconOnly?: false;
        text: ReactNode;
        withIcon?: boolean;
      }
    | {
        iconOnly: true;
        text: string;
        fallback?: ReactNode;
      }
  );

export const ResourceNameLink = (props: ResourceNameLinkProps) => {
  const { name, text, className, target: targetProp, rel: relProp } = props;
  const defaults = useContext(ResourceNameLinkDefaultsContext);
  const target = targetProp ?? defaults.target;
  const rel = relProp ?? defaults.rel;
  const extensions = useResourceLinkExtensions();
  const extension = extensions.find(candidate =>
    candidate.predicate({ name, text, className, target, rel }),
  );

  if (props.iconOnly) {
    const Icon = extension?.icon
      ? RESOURCE_LINK_ICONS[extension.icon]
      : undefined;

    if (!extension || !Icon) {
      return <>{props.fallback ?? null}</>;
    }

    return extension.render({
      name,
      text: <Icon size="small" title={props.text} />,
      className,
      target,
      rel,
    });
  }

  if (extension) {
    const DisplayName = extension.DisplayName;
    const Icon =
      props.withIcon && extension.icon
        ? RESOURCE_LINK_ICONS[extension.icon]
        : undefined;
    const displayText = DisplayName ? (
      <DisplayName name={name}>{text}</DisplayName>
    ) : (
      text
    );
    const content = Icon ? (
      <span className={styles.content}>
        <Icon className={styles.icon} size="small" title="" />
        <span className={styles.text}>{displayText}</span>
      </span>
    ) : (
      displayText
    );

    return extension.render({
      name,
      text: content,
      className,
      target,
      rel,
    });
  }

  return <strong data-resource-ref={name}>{text}</strong>;
};
